import React from 'react';
import { formatMoney, useCurrency } from '../context/CurrencyContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { itemLabel } from '../lib/activityItems';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { DayPicker as CalendarPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import {
  Calendar, Clock, Users, Check, ChevronRight,
  ChevronLeft, Loader2, Plus, Trash2, CreditCard, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatYen(amount, cur) {
  return formatMoney(amount, cur);
}

function ExperienceCard({ exp, isSelected, onSelect }) {
  return (
    <div
      onClick={onSelect}
      className={'p-4 rounded-xl border-2 cursor-pointer ' +
        (isSelected ? 'border-[#F9004D] bg-[#F9004D]/10' : 'border-white/10 hover:border-white/30')}
    >
      <div className="flex justify-between">
        <div>
          <h4 className="text-lg font-bold text-white">{exp.title}</h4>
          <p className="text-zinc-400 text-sm mt-1">{exp.description}</p>
          <div className="flex gap-3 mt-2 text-xs text-zinc-500">
            {exp.adult_rate && <span className="text-[#00F0FF]">Adulte: ¥{exp.adult_rate.toLocaleString()}/j</span>}
            {exp.child_rate && <span className="text-[#D4AF37]">Enfant: ¥{exp.child_rate.toLocaleString()}/j</span>}
          </div>
        </div>
        <div className={'w-6 h-6 rounded-full border-2 flex items-center justify-center ' +
          (isSelected ? 'border-[#F9004D] bg-[#F9004D]' : 'border-zinc-600')}>
          {isSelected && <Check className="h-4 w-4 text-white" />}
        </div>
      </div>
    </div>
  );
}

function ParticipantRow({ participant, index, onUpdate, onRemove, canRemove, adultRate, childRate }) {
  return (
    <div className="bg-zinc-800/50 rounded-xl p-4 border border-white/10">
      <div className="flex justify-between mb-3">
        <span className="text-zinc-300">Participant {index + 1}</span>
        {canRemove && (
          <button onClick={onRemove} className="text-red-400">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-400 text-sm mb-1 block">Nom</Label>
          <Input
            value={participant.name}
            onChange={(e) => onUpdate('name', e.target.value)}
            placeholder="Nom"
            className="bg-black/20 border-white/10 text-white"
          />
        </div>
        <div>
          <Label className="text-zinc-400 text-sm mb-1 block">Tarif</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => onUpdate('rate_type', 'adult')}
              className={'flex-1 ' + (participant.rate_type === 'adult' ? 'bg-[#00F0FF] text-black' : 'bg-zinc-800 text-zinc-300')}
            >Adulte</Button>
            <Button
              type="button"
              onClick={() => onUpdate('rate_type', 'child')}
              className={'flex-1 ' + (participant.rate_type === 'child' ? 'bg-[#D4AF37] text-black' : 'bg-zinc-800 text-zinc-300')}
            >Enfant</Button>
          </div>
        </div>
      </div>
      <div className="mt-2 text-right text-zinc-400 text-sm">
        {formatYen(participant.rate_type === 'adult' ? adultRate : childRate)}/jour
      </div>
    </div>
  );
}

export default function BookingFlow() {
  useCurrency(); // re-render quand la devise d'affichage change
  const { t } = useTranslation();
  const { guideId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [calendarOpen, setCalendarOpen] = React.useState(false);

  const [currentStep, setCurrentStep] = React.useState(1);
  const [guide, setGuide] = React.useState(null);
  const [experiences, setExperiences] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);

  const [selectedExperienceId, setSelectedExperienceId] = React.useState('');
  const [startDate, setStartDate] = React.useState('');
  const [numberOfDays, setNumberOfDays] = React.useState(1);
  const [participants, setParticipants] = React.useState([{ name: '', rate_type: 'adult' }]);
  const [priceDetails, setPriceDetails] = React.useState(null);

  React.useEffect(function loadGuide() {
    axios.get(API_URL + '/api/guides/' + guideId)
      .then(function(res) {
        setGuide(res.data);
        var exps = [];
        var activs = res.data.activities || [];
        for (var i = 0; i < activs.length; i++) {
          var e = activs[i];
          exps.push({
            id: e.activity_id,
            title: (e.name && (e.name.fr || e.name.en)) || e.location || '',
            description: (e.description && (e.description.fr || e.description.en)) || '',
            adult_rate: e.adult_rate,
            child_rate: e.child_rate,
            included: e.included || [],
            notIncluded: e.not_included || []
          });
        }
        setExperiences(exps);
        
        var expParam = searchParams.get('experience');
        if (expParam) {
          setSelectedExperienceId(expParam);
        } else if (exps.length > 0) {
          setSelectedExperienceId(exps[0].id);
        }
        setLoading(false);
      })
      .catch(function() {
        toast.error('Erreur de chargement');
        navigate('/hosts');
      });
  }, [guideId, navigate, searchParams]);

  React.useEffect(function calcPrice() {
    if (!selectedExperienceId || !startDate || numberOfDays < 1) return;
    var validP = [];
    for (var i = 0; i < participants.length; i++) {
      if (participants[i].name.trim()) {
        validP.push(participants[i]);
      }
    }
    if (validP.length === 0) return;
    
    axios.post(API_URL + '/api/bookings/calculate', {
      activity_id: selectedExperienceId,
      start_date: startDate,
      number_of_days: numberOfDays,
      participants: validP
    }, { withCredentials: true })
      .then(function(res) { setPriceDetails(res.data); })
      .catch(function(err) {
        var msg = err.response && err.response.data && err.response.data.detail;
        if (msg) toast.error(typeof msg === 'string' ? msg : 'Erreur de calcul du prix');
      });
  }, [selectedExperienceId, startDate, numberOfDays, participants, guideId]);

  function addParticipant() {
    if (participants.length >= 4) {
      toast.error('Maximum 6 participants');
      return;
    }
    setParticipants(participants.concat([{ name: '', rate_type: 'adult' }]));
  }

  function removeParticipant(idx) {
    if (participants.length <= 1) return;
    var newList = [];
    for (var i = 0; i < participants.length; i++) {
      if (i !== idx) newList.push(participants[i]);
    }
    setParticipants(newList);
  }

  function updateParticipant(idx, field, value) {
    var newList = [];
    for (var i = 0; i < participants.length; i++) {
      if (i === idx) {
        var p = { name: participants[i].name, rate_type: participants[i].rate_type };
        p[field] = value;
        newList.push(p);
      } else {
        newList.push(participants[i]);
      }
    }
    setParticipants(newList);
  }

  function handleSubmit() {
    var validP = [];
    for (var i = 0; i < participants.length; i++) {
      if (participants[i].name.trim()) {
        validP.push(participants[i]);
      }
    }
    if (!selectedExperienceId || !startDate || validP.length === 0) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setSubmitting(true);
    axios.post(API_URL + '/api/bookings/create', {
      activity_id: selectedExperienceId,
      start_date: startDate,
      number_of_days: numberOfDays,
      participants: validP,
      origin_url: window.location.origin
    }, { withCredentials: true })
      .then(function(res) {
        if (res.data.checkout_url) {
          window.location.href = res.data.checkout_url;
        }
      })
      .catch(function(err) {
        var msg = 'Erreur';
        if (err.response && err.response.data && err.response.data.detail) {
          msg = err.response.data.detail;
        }
        toast.error(msg);
        setSubmitting(false);
      });
  }

  var today = new Date().toISOString().split('T')[0];
  var selectedExp = null;
  for (var i = 0; i < experiences.length; i++) {
    if (experiences[i].id === selectedExperienceId) {
      selectedExp = experiences[i];
      break;
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-20">
        <Loader2 className="h-12 w-12 text-[#F9004D] animate-spin" />
      </div>
    );
  }

  var adultRate = selectedExp ? (selectedExp.adult_rate || 38000) : (guide ? guide.adult_rate || 38000 : 38000);
  var childRate = selectedExp ? (selectedExp.child_rate || 20000) : (guide ? guide.child_rate || 20000 : 20000);

  // Render experiences
  var experienceCards = [];
  for (var j = 0; j < experiences.length; j++) {
    var exp = experiences[j];
    experienceCards.push(
      <ExperienceCard
        key={exp.id}
        exp={exp}
        isSelected={selectedExperienceId === exp.id}
        onSelect={function(id) { return function() { setSelectedExperienceId(id); }; }(exp.id)}
      />
    );
  }

  // Render participants
  var participantRows = [];
  for (var k = 0; k < participants.length; k++) {
    participantRows.push(
      <ParticipantRow
        key={k}
        participant={participants[k]}
        index={k}
        onUpdate={function(idx) { return function(f, v) { updateParticipant(idx, f, v); }; }(k)}
        onRemove={function(idx) { return function() { removeParticipant(idx); }; }(k)}
        canRemove={participants.length > 1}
        adultRate={adultRate}
        childRate={childRate}
      />
    );
  }

  // Render summary participants
  var summaryParticipants = [];
  if (priceDetails && priceDetails.participants) {
    for (var m = 0; m < priceDetails.participants.length; m++) {
      var pp = priceDetails.participants[m];
      summaryParticipants.push(
        <div key={m} className="flex justify-between text-white">
          <span>{pp.name} <span className="text-xs text-zinc-500">({pp.rate_type})</span></span>
          <span>{formatYen(pp.rate_amount)}/jour</span>
        </div>
      );
    }
  }

  // Render included items
  var includedItems = [];
  if (selectedExp && selectedExp.included) {
    for (var n = 0; n < selectedExp.included.length; n++) {
      includedItems.push(
        <p key={n} className="text-green-400 text-sm"><Check className="inline h-3 w-3" /> {itemLabel(t, selectedExp.included[n])}</p>
      );
    }
  }

  // Progress steps
  var progressSteps = [];
  for (var s = 1; s <= 4; s++) {
    progressSteps.push(
      <div key={s} className="flex items-center">
        <div className={'w-10 h-10 rounded-full flex items-center justify-center font-bold ' + 
          (currentStep >= s ? 'bg-[#F9004D] text-white' : 'bg-zinc-800 text-zinc-500')}>
          {currentStep > s ? <Check className="h-5 w-5" /> : s}
        </div>
        {s < 4 && <div className={'w-12 h-1 mx-1 ' + (currentStep > s ? 'bg-[#F9004D]' : 'bg-zinc-800')} />}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Progress */}
        <div className="flex justify-between mb-8">
          {progressSteps}
        </div>

        {/* Guide Header */}
        <Card className="bg-zinc-900/50 border-white/10 p-4 mb-6">
          <div className="flex items-center gap-4">
            <img
              src={guide && guide.profile_photo_url ? guide.profile_photo_url : "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%2327272a'/%3E%3Ccircle cx='50' cy='40' r='16' fill='%2352525b'/%3E%3Cpath d='M22 82c0-15 12-24 28-24s28 9 28 24' fill='%2352525b'/%3E%3C/svg%3E"}
              alt={guide ? guide.user_name : ''}
              className="w-16 h-16 rounded-full object-cover object-top border-2 border-[#F9004D]"
            />
            <div>
              <h2 className="text-xl font-bold text-white">{guide ? guide.user_name : ''}</h2>
              <div className="flex gap-4 mt-1 text-sm">
                <span className="text-[#00F0FF]">Adulte: {formatYen(adultRate)}/jour</span>
                <span className="text-[#D4AF37]">Enfant: {formatYen(childRate)}/jour</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Step 1: Experience */}
        {currentStep === 1 && (
          <Card className="bg-zinc-900/50 border-white/10 p-6">
            <h3 className="text-xl font-bold text-white mb-6">
              <Clock className="inline h-5 w-5 mr-2 text-[#F9004D]" />
              Choisissez votre expérience
            </h3>
            <div className="space-y-3">
              {experienceCards}
            </div>
          </Card>
        )}

        {/* Step 2: Date */}
        {currentStep === 2 && (
          <Card className="bg-zinc-900/50 border-white/10 p-6">
            <h3 className="text-xl font-bold text-white mb-6">
              <Calendar className="inline h-5 w-5 mr-2 text-[#F9004D]" />
              Date et durée
            </h3>
            <div className="space-y-6">
              <div>
                <Label className="text-zinc-300 mb-2 block">Date de départ</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={'w-full flex items-center gap-3 bg-black/20 border border-white/10 rounded-md py-5 px-5 text-left hover:border-[#F9004D]/60 transition-colors ' + (startDate ? 'text-white' : 'text-zinc-400')}
                    >
                      <Calendar className="h-6 w-6 text-[#F9004D] shrink-0" />
                      <span className="text-lg">{startDate ? new Date(startDate + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Choisir une date'}</span>
                      <ChevronRight className="h-5 w-5 ml-auto text-zinc-500" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto p-0 bg-zinc-900 border-white/10 text-white">
                    <CalendarPicker
                      mode="single"
                      selected={startDate ? new Date(startDate + 'T00:00:00') : undefined}
                      onSelect={function(d) {
                        if (d) {
                          setStartDate(d.toISOString().split('T')[0]);
                          setCalendarOpen(false);
                        }
                      }}
                      disabled={{ before: new Date() }}
                      initialFocus
                      classNames={{
                        months: 'flex flex-col',
                        month: 'space-y-4 p-2',
                        caption: 'flex justify-center pt-1 relative items-center mb-2',
                        caption_label: 'text-lg font-bold text-white',
                        nav_button: 'h-9 w-9 bg-zinc-800 hover:bg-zinc-700 rounded-md text-white flex items-center justify-center',
                        nav_button_previous: 'absolute left-1',
                        nav_button_next: 'absolute right-1',
                        head_cell: 'text-zinc-400 w-12 font-normal text-sm',
                        cell: 'relative p-0 text-center',
                        day: 'h-12 w-12 p-0 font-normal text-base rounded-md text-zinc-200 hover:bg-zinc-700 aria-selected:opacity-100',
                        day_selected: 'bg-[#F9004D] text-white hover:bg-[#F9004D] focus:bg-[#F9004D]',
                        day_today: 'bg-zinc-800 text-[#00F0FF] font-bold',
                        day_disabled: 'text-zinc-600 opacity-40 cursor-not-allowed'
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-zinc-300 mb-2 block">Nombre de jours</Label>
                <div className="flex items-center gap-4">
                  <Button variant="outline" onClick={function() { setNumberOfDays(Math.max(1, numberOfDays - 1)); }} className="h-12 w-12 border-white/20 text-white">-</Button>
                  <div className="text-4xl font-bold text-white w-20 text-center">{numberOfDays}</div>
                  <Button variant="outline" onClick={function() { setNumberOfDays(numberOfDays + 1); }} className="h-12 w-12 border-white/20 text-white">+</Button>
                  <span className="text-zinc-400">jour{numberOfDays > 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Step 3: Participants */}
        {currentStep === 3 && (
          <Card className="bg-zinc-900/50 border-white/10 p-6">
            <h3 className="text-xl font-bold text-white mb-6">
              <Users className="inline h-5 w-5 mr-2 text-[#F9004D]" />
              Participants
            </h3>
            <div className="space-y-4">
              {participantRows}
              {participants.length < 4 && (
                <Button variant="outline" onClick={addParticipant} className="w-full border-dashed border-white/20 text-zinc-300">
                  <Plus className="h-4 w-4 mr-2" /> Ajouter
                </Button>
              )}
              {participants.length >= 4 && (
                <div className="flex items-center gap-2 text-amber-400 bg-amber-400/10 rounded-lg p-3 text-sm">
                  <AlertCircle className="h-4 w-4" /> Maximum 6 participants
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Step 4: Summary */}
        {currentStep === 4 && (
          <Card className="bg-zinc-900/50 border-white/10 p-6">
            <h3 className="text-xl font-bold text-white mb-6">
              <CreditCard className="inline h-5 w-5 mr-2 text-[#F9004D]" />
              Récapitulatif
            </h3>
            <div className="space-y-4">
              <div className="border-b border-white/10 pb-4">
                <p className="text-zinc-400 text-sm">EXPÉRIENCE</p>
                <p className="text-white text-lg font-bold">{selectedExp ? selectedExp.title : ''}</p>
                <p className="text-zinc-400 text-sm">avec {guide ? guide.user_name : ''}</p>
              </div>
              <div className="border-b border-white/10 pb-4">
                <p className="text-zinc-400 text-sm">DATE</p>
                <p className="text-white">{startDate}</p>
                <p className="text-[#00F0FF] font-bold">{numberOfDays} jour{numberOfDays > 1 ? 's' : ''}</p>
              </div>
              <div className="border-b border-white/10 pb-4">
                <p className="text-zinc-400 text-sm">PARTICIPANTS</p>
                {summaryParticipants}
              </div>
              {includedItems.length > 0 && (
                <div className="border-b border-white/10 pb-4">
                  <p className="text-zinc-400 text-sm">INCLUS</p>
                  {includedItems}
                </div>
              )}
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <div className="flex justify-between text-zinc-300 mb-2">
                  <span>Sous-total / jour</span>
                  <span>{formatYen(priceDetails ? priceDetails.subtotal_per_day : 0)}</span>
                </div>
                <div className="flex justify-between text-zinc-300 mb-2">
                  <span>× {numberOfDays} jour{numberOfDays > 1 ? 's' : ''}</span>
                  <span>{formatYen(priceDetails ? (priceDetails.base_amount || 0) : 0)}</span>
                </div>
                <div className="flex justify-between text-zinc-400 text-sm mb-4">
                  <span>Frais de traitement</span>
                  <span>{formatYen(priceDetails ? (priceDetails.service_fee || 0) : 0)}</span>
                </div>
                <div className="border-t border-white/10 pt-4 flex justify-between items-center">
                  <span className="text-xl font-bold text-white">TOTAL</span>
                  <span className="text-3xl font-bold text-[#F9004D]">{formatYen(priceDetails ? priceDetails.total_price : 0)}</span>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          {currentStep > 1 ? (
            <Button onClick={function() { setCurrentStep(currentStep - 1); }} variant="outline" className="border-white/20 text-white">
              <ChevronLeft className="h-4 w-4 mr-2" /> Retour
            </Button>
          ) : (
            <Button onClick={function() { navigate('/guides/' + guideId); }} variant="outline" className="border-white/20 text-white">
              <ChevronLeft className="h-4 w-4 mr-2" /> Annuler
            </Button>
          )}

          {currentStep < 4 ? (
            <Button 
              onClick={function() { setCurrentStep(currentStep + 1); }} 
              disabled={(currentStep === 1 && !selectedExperienceId) || (currentStep === 2 && !startDate)}
              className="bg-[#F9004D] hover:bg-[#D00040] text-white disabled:opacity-50"
            >
              Continuer <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="bg-[#F9004D] hover:bg-[#D00040] text-white px-8">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
              Payer {formatYen(priceDetails ? priceDetails.total_price : 0)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
